import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/programs/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json())
    ])
      .then(([programsData, settingsData]) => {
        setPrograms(programsData);
        setSettings(settingsData);
      })
      .catch(err => console.error('Error fetching data:', err))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title="البرامج الأكاديمية"
        description="استعرض البرامج الأكاديمية المتميزة في جامعة ابن النفيس للعلوم الطبية. برامج البكالوريوس والماجستير في طب الأسنان، الصيدلة، العلاج الطبيعي والعلوم الطبية."
        keywords="برامج أكاديمية, بكالوريوس طب أسنان, ماجستير, صيدلة, علاج طبيعي, جامعة ابن النفيس"
        url="https://uni-content-fixer.preview.emergentagent.com/programs"
      />
      
      {/* Hero Section */}
      <PageHero 
        title="البرامج الأكاديمية"
        subtitle="نقدم مجموعة متنوعة من البرامج الطبية المتخصصة"
        backgroundImage={settings?.page_programs_bg}
      />

      {/* Programs Grid */}
      <section className="py-20 bg-gray-50 dark:bg-[#071326]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {programs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">لا توجد برامج حالياً</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programs.map((program) => (
                <Card key={program.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 dark:bg-[#0E2243] dark:border-gray-800">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={program.image || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800'}
                      alt={program.title_ar}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 right-4 bg-[#C9A040] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {program.duration}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-[#C9A040] font-semibold mb-2">
                      <GraduationCap className="w-4 h-4" />
                      {program.degree}
                    </div>
                    <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-3 group-hover:text-[#C9A040] transition-colors">
                      {program.title_ar}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">{program.description_ar}</p>
                    <Link to={`/programs/${program.id}`}>
                      <Button variant="link" className="text-[#C9A040] hover:text-[#0F388E] dark:hover:text-[#FACC15] p-0 font-medium">
                        اقرأ المزيد
                        <ArrowLeft className="mr-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Programs;
